/**
 * StatChip — the one chip markup every card face shares (design system 02
 * §3.1): `.cards-card-stat` holding an icon and a value. The look is one CSS
 * recipe (kit.css.ts); this keeps the markup one too, so a card in the hand,
 * on the board, in the plan, in flight and in a swallow carries the same chip.
 * Whatever the chip does not own — testids, playback data, aria — passes
 * through in the caller's order.
 */
import type { HTMLAttributes, ReactElement, ReactNode } from 'react';
import { PixelIcon } from './PixelIcon.js';

export interface StatChipProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  icon: 'attack' | 'health' | 'energy';
  value: ReactNode;
  /** `data-len` on the value: a three-digit stat steps down a size. */
  len?: number | undefined;
  /** The value is read from the chip's own aria-label instead. */
  valueHidden?: boolean;
  /** Rendered after the value (a playback delta). */
  children?: ReactNode;
}

export function StatChip({ icon, value, len, valueHidden = false, children, ...rest }: StatChipProps): ReactElement {
  return (
    <span className="cards-card-stat" {...rest}>
      <span className="cards-card-stat-icon" aria-hidden="true"><PixelIcon name={icon} /></span>{' '}
      <span
        className="cards-card-stat-value"
        {...(len === undefined ? {} : { 'data-len': len })}
        {...(valueHidden ? { 'aria-hidden': true } : {})}
      >
        {value}
      </span>
      {children}
    </span>
  );
}
